<template>
  <nav
    id="codeEditorMenu"
    ref="codeEditorMenu"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    class="ui right vertical sidebar labeled menu thin visible"
    :class="[
      {
        expanded: expanded,
        contracted: !expanded,
        inverted: !expanded
      },
      colorClass
    ]"
  >
    <div class="item">
      <div class="header">{{ $t('EDITORSETTINGS') }}</div>

      <div class="menu">
        <a href="#" id="beautifyItem" class="item" @click.prevent="beautifyCode">
          <i class="magic icon"></i>
          {{ $t('BEAUTIFY_CODE') }}
        </a>

        <a href="#" class="item" @click.prevent="togglePinned">
          <i class="icon toggle" :class="{ on: pinned, off: !pinned }"></i>
          {{ $tc('PINMENU', 2) }}
        </a>

        <show-hide
          :shouldShow="showMinimap"
          :onClick="onShowMinimapChange"
          :showMessage="$t('SHOW_MINIMAP')"
          :hideMessage="$t('SHOW_MINIMAP')"
        />
      </div>
    </div>
    <div class="scroller">
      <!-- Search -->
      <div class="item">
        <div class="header">
          {{ $t('SEARCH_IN', { thing: $tc('SNIPPET', 2) }) }}
        </div>
        <div class="menu">
          <div class="search item">
            <div class="ui transparent icon input">
              <input
                type="text"
                :placeholder="$t('SEARCH')"
                v-model="suggestionSearchTerm"
                @keydown.esc="suggestionSearchTerm = ''"
                autocomplete="off"
              />
              <i class="search link icon"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Suggestions/Snippets -->
      <div class="item" v-if="searching && displayableSuggestions.length === 0">
        <div class="ui message">
          <p>{{ $t('CODE_EDITOR_MENU_SEARCH_NO_RESULTS') }}</p>
        </div>
      </div>
      <template v-for="type in suggestionTypes">
        <div class="title item" :key="type" @click="toggleTypeExpando(type.toLowerCase())">
          <i
            class="angle icon"
            v-if="!searching"
            :class="{
              left: !typeExpando[type.toLowerCase()],
              down: typeExpando[type.toLowerCase()]
            }"
          ></i>
          <div class="capitalized">{{ $t('SUGGESTION', { type: type }) }}</div>
          <div
            class="menu"
            :key="type + 'menu'"
            v-if="searching || typeExpando[type.toLowerCase()]"
          >
            <a
              class="item"
              @click.stop.prevent="handleInsert(s.text)"
              v-for="(s, index) in displayableSuggestions[type.toLowerCase()]"
              :key="type + index"
              :title="s.documentation"
            >
              <i class="plus icon"></i>
              {{ s.display || s.text }}

              <!-- Link to documentation -->
              <a v-if="s.link" @click.stop.prevent="openTab(s.link)">
                <i class="info circle icon"></i>
              </a>
            </a>
          </div>
        </div>
      </template>
      <div class="item" v-if="suggestionsOverflowing">
        <div class="ui warning message">
          <p>{{ $t('CODE_EDITOR_MENU_OVERFLOW_MESSAGE') }}</p>
        </div>
      </div>
    </div>
  </nav>
</template>

<style lang="scss" scoped="true">
@charset "utf-8";

#codeEditorMenu {
  width: 300px !important;
  overflow-x: hidden;
  margin-bottom: 1em;
  display: flex;

  transition: transform 0.5s;
  transition-delay: 100ms;

  &.expanded {
    transform: translate3d(0, 0, 0);
  }

  &.contracted {
    transform: translate3d(290px, 0, 0);
    * {
      opacity: 0;
    }
  }

  .scroller {
    overflow-y: auto;
    overflow-x: hidden;

    .header,
    .item.title {
      cursor: pointer;
      .capitalized:first-letter {
        text-transform: uppercase;
      }
    }
  }
}
</style>

<script>
// @flow
import { about } from 'utility'
import { ShowHide } from 'ui'
import _ from 'lodash'

const MAX_RENDERED_SUGGESTIONS = 500
const MIN_SEARCH_CHARS = 2

export default {
  name: 'CodeEditorMenu',
  components: {
    ShowHide
  },
  props: ['suggestions', 'handleInsert', 'beautifyCode'],
  data() {
    return {
      expanded: false,
      pinned: false,
      suggestionSearchTerm: '',
      allSuggestionsForInput: [],
      allSuggestions: [],
      typeExpando: {},
      showMinimap: false,
      minSearchChars: MIN_SEARCH_CHARS
    }
  },

  computed: {
    searching() {
      return this.suggestionSearchTerm && this.suggestionSearchTerm.length > 0
    },

    displayableSuggestions() {
      return this.suggestionSearchTerm ? this.cappedSuggestionsForInput : this.allSuggestionsGrouped
    },

    colorClass() {
      return this.$store.state.config.color
    },

    allSuggestionsGrouped() {
      return _.groupBy(this.allSuggestions, s => s.type.toLowerCase())
    },

    suggestionTypes() {
      return Object.keys(this.displayableSuggestions)
        .map(type => type.toLowerCase())
        .sort()
    },

    cappedSuggestionsForInput() {
      return _.groupBy(_.take(this.allSuggestionsForInput, MAX_RENDERED_SUGGESTIONS), s =>
        s.type.toLowerCase()
      )
    },

    suggestionsOverflowing() {
      return this.allSuggestionsForInput.length > MAX_RENDERED_SUGGESTIONS
    }
  },

  methods: {
    togglePinned() {
      this.pinned = !this.pinned
      this.$emit('editorMenuPinnedChanged', this.pinned)
    },
    openTab(url) {
      window.open(url)
    },
    toggleTypeExpando(type) {
      this.$set(this.typeExpando, type, !this.typeExpando[type])
    },

    onShowMinimapChange() {
      this.showMinimap = !this.showMinimap
      this.$emit('showMinimap', this.showMinimap)
    },
    onMouseLeave() {
      if (this.pinned) return
      this.expanded = false
    },
    onMouseEnter() {
      this.expanded = true
    }
  },

  mounted() {
    this.suggestions('', true).then(results => {
      this.allSuggestions = results
    })
  },

  watch: {
    suggestions: function() {
      this.suggestions('', true).then(results => (this.allSuggestions = results))
    },
    suggestionSearchTerm: function(term) {
      if (!this.suggestionSearchTerm || this.suggestionSearchTerm.length < MIN_SEARCH_CHARS) return
      this.suggestions(this.suggestionSearchTerm, true).then(
        results => (this.allSuggestionsForInput = results)
      )
    },
    expanded: {
      handler(expanded) {
        if (!expanded) {
          this.suggestionSearchTerm = ''
        }
      }
    }
  }
}
//
</script>
